import { useState, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebaseConfig';

interface RunData {
  runNumber: number;
  recordCount: number;
  upfrontCents: number;
  trailCents: number;
  totalCents: number;
}

interface MonthData {
  periodMonth: string;
  runs: RunData[];
}

export interface EntityReport {
  entityCode: string;
  legalEntityName: string;
  dateRange: { first: string; last: string };
  totalRecords: number;
  upfrontCents: number;
  trailCents: number;
  totalCents: number;
  months: MonthData[];
}

export interface GapReport {
  entityCode: string;
  legalEntityName: string;
  missingMonths: string[];
  sparseRuns: Array<{
    month: string;
    presentRuns: number[];
    typicalRunCount: number;
  }>;
}

export interface VerificationReport {
  success: boolean;
  companyId: string;
  entities: EntityReport[];
  gaps: GapReport[];
  grandTotal: {
    records: number;
    upfrontCents: number;
    trailCents: number;
    totalCents: number;
  };
}

export function useUploadVerificationReport() {
  const [data, setData] = useState<VerificationReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async (companyId: string, entityCode?: string, startMonth?: string, endMonth?: string) => {
    setLoading(true);
    setError(null);
    try {
      const fn = httpsCallable(functions, 'getUploadVerificationReport');
      const result = await fn({ companyId, entityCode, startMonth, endMonth }) as any;
      if (result.data.success) {
        setData(result.data);
      } else {
        setError(result.data.error || 'Failed to load report');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  }, []);

  return { data, loading, error, fetch };
}
